import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Ruler, FileCheck, ChevronDown, AlertTriangle } from "lucide-react";
import { api, fmtDateUS, formatApiErrorDetail } from "@/lib/api";

const inputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-4 py-4 text-base text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";
const numInputCls = "rounded-lg w-full bg-white/5 border border-white/15 px-3 py-3 text-base text-white placeholder:text-white/35 focus:outline-none focus:border-primary transition-colors duration-300";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const num = (v) => { const x = parseFloat(v); return Number.isFinite(x) && x > 0 ? x : 0; };
const money = (v) => `$${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qtyFmt = (v) => (Math.round((v || 0) * 100) / 100).toString();

/* Calculator-style expression evaluation — mirrors the backend parser exactly.
   Supports + - * / (also x, X, ×, ÷), decimals, parentheses, unary minus. */
const evalExpr = (src) => {
  if (!src || !String(src).trim()) return { value: 0, error: "empty" };
  const compact = String(src).replace(/×/g, "*").replace(/÷/g, "/").replace(/[xX]/g, "*").replace(/\s+/g, "");
  const tokens = compact.match(/\d*\.?\d+|[+\-*/()]/g) || [];
  if (!tokens.length || tokens.join("") !== compact) return { value: 0, error: "invalid" };
  let pos = 0;
  const peek = () => tokens[pos];
  function factor() {
    if (pos >= tokens.length) throw new Error("incomplete");
    const tok = tokens[pos];
    if (tok === "-" || tok === "+") { pos += 1; const f = factor(); return tok === "-" ? -f : f; }
    if (tok === "(") { pos += 1; const v = expr(); if (tokens[pos] !== ")") throw new Error("missing )"); pos += 1; return v; }
    if (tok === ")") throw new Error("unexpected )");
    pos += 1; return parseFloat(tok);
  }
  function term() {
    let v = factor();
    while (peek() === "*" || peek() === "/") {
      const op = tokens[pos]; pos += 1;
      const r = factor();
      if (op === "/") { if (r === 0) throw new Error("div0"); v /= r; } else v *= r;
    }
    return v;
  }
  function expr() {
    let v = term();
    while (peek() === "+" || peek() === "-") {
      const op = tokens[pos]; pos += 1;
      const r = term();
      v = op === "+" ? v + r : v - r;
    }
    return v;
  }
  try {
    const v = expr();
    if (pos !== tokens.length) throw new Error("trailing");
    return { value: Math.round(v * 10000) / 10000, error: null };
  } catch {
    return { value: 0, error: "invalid" };
  }
};

const dedTotal = (d) => {
  const one = d.mode === "area" ? num(d.area) : num(d.width) * num(d.height);
  return one * num(d.quantity);
};

const familyOf = (unit) => (unit === "SQ" || unit === "SQFT" ? "area" : unit === "LNFT" ? "linear" : unit === "EACH" ? "count" : "flat");
const FAMILY_UNIT = { area: "SQFT", linear: "LNFT", count: "EA", flat: "FLAT" };
const round2 = (v) => Math.round(v * 100) / 100;

/* Per-item math, mirrors backend compute_measurement:
   expression rows -> gross -> category deductions (once per item) -> category waste -> final qty x price */
const itemCalc = (item, deds, wasteMap) => {
  const family = familyOf(item.unit);
  let gross = 0;
  let hasError = false;
  (item.rows || []).forEach((r) => {
    const { value, error } = evalExpr(r.expression);
    if (error && error !== "empty") hasError = true;
    if (!error) gross += value;
  });
  gross = round2(gross);
  let ded = 0;
  if (family === "area") {
    deds.forEach((d) => {
      if (d.category !== item.category) return;
      if (d.item_ids.length && !d.item_ids.includes(item.id)) return;
      ded += dedTotal(d);
    });
  }
  ded = round2(ded);
  const net = Math.max(0, round2(gross - ded));
  const waste = num(wasteMap[item.category]);
  const withWaste = round2(net * (1 + waste / 100));
  const finalQty = item.unit === "FLAT" ? 1 : item.unit === "SQ" ? round2(withWaste / 100) : withWaste;
  return { family, gross, ded, net, waste, withWaste, finalQty, hasError, line: round2(finalQty * item.price) };
};

const blankSheet = () => ({
  id: null, project_name: "", client_name: "", client_email: "", client_phone: "",
  address: "", billing_address: "", notes: "",
  deductions: [], items: [], category_waste: {}, adjustment_pct: "", adjustment_fixed: "",
  linked_proposal_id: null, status: "draft",
});

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const SectionTitle = ({ children, sub }) => (
  <div>
    <h2 className="font-display text-2xl tracking-wide">{children}</h2>
    {sub && <p className="text-white/40 text-xs mt-1">{sub}</p>}
  </div>
);

export const MeasurementsTab = () => {
  const [view, setView] = useState("list");
  const [sheets, setSheets] = useState([]);
  const [sheet, setSheet] = useState(blankSheet());
  const [delPin, setDelPin] = useState(null); // null = delete confirm closed
  const [library, setLibrary] = useState([]);
  const [catOrder, setCatOrder] = useState([]);
  const [prefill, setPrefill] = useState({ customers: [], proposals: [] });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickCat, setPickCat] = useState(null);
  const [pickItem, setPickItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [closedCats, setClosedCats] = useState({});
  const [clientMode, setClientMode] = useState("new");

  const loadSheets = useCallback(() => {
    api.get("/admin/measurements").then(({ data }) => setSheets(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadSheets();
    api.get("/admin/library").then(({ data }) => setLibrary(data.filter((i) => i.category !== "My Costs"))).catch(() => {});
    api.get("/admin/library/categories").then(({ data }) => {
      const all = [...Object.keys(data.defaults || {}), ...(data.custom || []).map((c) => c.name)];
      const ordered = [...(data.order || []).filter((c) => all.includes(c)), ...all.filter((c) => !(data.order || []).includes(c))];
      setCatOrder(ordered.filter((c) => c !== "My Costs"));
    }).catch(() => {});
    api.get("/admin/measurements/prefill-sources").then(({ data }) => setPrefill(data)).catch(() => {});
  }, [loadSheets]);

  /* categories present on this sheet, in library order */
  const sheetCats = useMemo(() => {
    const present = [...new Set(sheet.items.map((i) => i.category))];
    return [...catOrder.filter((c) => present.includes(c)), ...present.filter((c) => !catOrder.includes(c))];
  }, [sheet.items, catOrder]);

  const calcs = useMemo(() => {
    const m = {};
    sheet.items.forEach((it) => { m[it.id] = itemCalc(it, sheet.deductions, sheet.category_waste); });
    return m;
  }, [sheet.items, sheet.deductions, sheet.category_waste]);

  const totals = useMemo(() => {
    const subtotal = Object.values(calcs).reduce((s, c) => s + c.line, 0);
    const adjAmt = subtotal * (num(sheet.adjustment_pct) / 100);
    const fixed = num(sheet.adjustment_fixed);
    return { subtotal: round2(subtotal), adjAmt: round2(adjAmt), fixed: round2(fixed), total: round2(subtotal + adjAmt + fixed) };
  }, [calcs, sheet.adjustment_pct, sheet.adjustment_fixed]);

  const upd = (patch) => setSheet((s) => ({ ...s, ...patch }));
  const updDed = (did, patch) => upd({ deductions: sheet.deductions.map((d) => (d.id === did ? { ...d, ...patch } : d)) });
  const updItem = (iid, patch) => upd({ items: sheet.items.map((i) => (i.id === iid ? { ...i, ...patch } : i)) });

  const openSheet = async (id) => {
    try {
      const { data } = await api.get(`/admin/measurements/${id}`);
      const calcItems = data.calc?.items || [];
      const items = (data.items || []).map((it) => {
        const c = calcItems.find((ci) => ci.id === it.id) || {};
        return { ...it, rows: it.rows || [], name: c.name || "Removed library item", category: c.category || "", unit: c.unit || "EACH", price: c.unit_price ?? 0, subcategory: c.subcategory || "", missing: !!c.missing };
      });
      const deductions = (data.deductions || []).map((d) => ({ ...d, mode: num(d.area) > 0 ? "area" : "wh", item_ids: d.item_ids || [] }));
      setSheet({ ...blankSheet(), ...data, items, deductions, category_waste: data.category_waste || {}, adjustment_pct: data.adjustment_pct || "", adjustment_fixed: data.adjustment_fixed || "" });
      setView("edit");
    } catch {
      toast.error("Could not open the sheet");
    }
  };

  const applyPrefill = (val) => {
    if (!val) return;
    const [kind, id] = val.split(":");
    if (kind === "cust") {
      const c = prefill.customers.find((x) => x.id === id);
      if (c) upd({ client_name: c.name || "", client_email: c.email || "", client_phone: c.phone || "", address: c.address || sheet.address, billing_address: c.address || sheet.billing_address });
    } else {
      const p = prefill.proposals.find((x) => x.id === id);
      if (p) upd({ project_name: p.title || sheet.project_name, client_name: p.client_name || "", client_email: p.client_email || "", client_phone: p.client_phone || "", address: p.project_address || p.client_address || "", billing_address: p.client_address || "" });
    }
  };

  const addItem = (lib, option) => {
    const price = option
      ? (option.labor_rate || 0) + (option.material_rate || 0)
      : (lib.labor_rate || 0) + (lib.material_rate || 0) || lib.default_rate || 0;
    setSheet((s) => ({
      ...s,
      items: [...s.items, {
        id: uid(), library_item_id: lib.id, subcategory_id: option ? option.id : "",
        name: lib.name, subcategory: option ? option.name : "", category: lib.category,
        unit: lib.unit, price, rows: [], missing: false,
      }],
    }));
    setPickerOpen(false);
    setPickCat(null);
    setPickItem(null);
  };

  const payload = () => ({
    project_name: sheet.project_name, client_name: sheet.client_name, client_email: sheet.client_email,
    client_phone: sheet.client_phone, address: sheet.address, billing_address: sheet.billing_address,
    notes: sheet.notes,
    items: sheet.items.map((i) => ({
      id: i.id, library_item_id: i.library_item_id, subcategory_id: i.subcategory_id,
      rows: (i.rows || []).map((r) => ({ id: r.id, label: r.label, expression: r.expression })),
    })),
    deductions: sheet.deductions.map((d) => ({
      id: d.id, title: d.title, category: d.category || "",
      item_ids: (d.item_ids || []).filter((x) => sheet.items.some((i) => i.id === x)),
      width: d.mode === "area" ? 0 : num(d.width), height: d.mode === "area" ? 0 : num(d.height),
      area: d.mode === "area" ? num(d.area) : 0, quantity: d.quantity === "" ? 1 : num(d.quantity),
    })),
    category_waste: Object.fromEntries(Object.entries(sheet.category_waste || {}).map(([k, v]) => [k, num(v)])),
    adjustment_pct: num(sheet.adjustment_pct), adjustment_fixed: num(sheet.adjustment_fixed),
  });

  const save = async (quiet = false) => {
    setSaving(true);
    try {
      let id = sheet.id;
      if (id) {
        await api.put(`/admin/measurements/${id}`, payload());
      } else {
        const { data } = await api.post("/admin/measurements", payload());
        id = data.id;
        setSheet((s) => ({ ...s, id }));
      }
      if (!quiet) toast.success("Measurement sheet saved");
      loadSheets();
      return id;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const createEstimate = async () => {
    setEstimating(true);
    try {
      const targetId = await save(true);
      if (!targetId) return;
      const { data } = await api.post(`/admin/measurements/${targetId}/create-estimate`);
      setSheet((s) => ({ ...s, id: targetId, status: "estimated", linked_proposal_id: data.proposal_id }));
      toast.success(`Estimate draft ${sheet.linked_proposal_id ? "updated" : "created"} — ${money(data.total)}. Review it in the Proposals tab.`);
      loadSheets();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEstimating(false);
    }
  };

  const removeSheet = async () => {
    try {
      await api.delete(`/admin/measurements/${sheet.id}`, { params: { pin: delPin } });
      toast.success("Sheet deleted");
      setDelPin(null);
      setView("list");
      loadSheets();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete");
    }
  };

  if (view === "list") {
    return (
      <div data-testid="measurements-tab" className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-4xl tracking-wide">MEASUREMENTS</h1>
            <p className="text-white/40 text-xs mt-1">Pick a library item, type the math, get a priced estimate.</p>
          </div>
          <button onClick={() => { setSheet(blankSheet()); setView("edit"); }} data-testid="meas-new-btn" className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-3 text-xs font-bold uppercase tracking-widest transition-transform duration-300 hover:-translate-y-0.5 shrink-0">
            <Plus size={15} aria-hidden="true" /> New
          </button>
        </div>
        {sheets.length === 0 && (
          <div className="border border-dashed border-white/15 rounded-xl py-14 text-center space-y-3" data-testid="meas-empty">
            <Ruler size={28} className="mx-auto text-primary" aria-hidden="true" />
            <p className="text-white/40 text-sm">No measurement sheets yet.<br />Tap "New" at the job site to start one.</p>
          </div>
        )}
        <div className="space-y-3">
          {sheets.map((m) => (
            <button key={m.id} onClick={() => openSheet(m.id)} data-testid={`meas-card-${m.id}`} className="w-full border border-white/10 rounded-xl bg-card shadow-[0_4px_20px_rgba(0,0,0,0.3)] p-4 text-left transition-colors duration-300 hover:border-primary/50">
              <div className="flex items-center justify-between gap-3">
                <p className="font-bold truncate">{m.project_name || "Untitled sheet"}</p>
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 shrink-0 ${m.status === "estimated" ? "bg-emerald-400/20 text-emerald-300" : "bg-white/10 text-white/60"}`} data-testid={`meas-status-${m.id}`}>
                  {m.status === "estimated" ? "Estimate created" : "Draft"}
                </span>
              </div>
              <p className="text-xs text-white/50 mt-1 truncate">{m.client_name || "No customer"}{m.address ? ` · ${m.address}` : ""}</p>
              <p className="text-sm mt-2"><span className="text-primary font-bold">{money(m.total)}</span><span className="text-white/40 text-xs"> · {m.item_count} item{m.item_count === 1 ? "" : "s"} · {m.updated_at ? fmtDateUS(m.updated_at) : ""}</span></p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="measurement-editor" className="space-y-8">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => { setView("list"); loadSheets(); }} data-testid="meas-back" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-primary transition-colors duration-300">
          <ArrowLeft size={15} aria-hidden="true" /> All Sheets
        </button>
        <button onClick={() => save()} disabled={saving} data-testid="meas-save-draft" className="border border-primary/60 text-primary px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors duration-300 hover:bg-primary hover:text-primary-foreground disabled:opacity-50">
          {saving ? "Saving..." : "Save Draft"}
        </button>
      </div>

      {/* 1 — PROJECT INFORMATION */}
      <section className="space-y-4">
        <SectionTitle sub="Start a brand-new customer, or find one that already exists.">1 · PROJECT INFORMATION</SectionTitle>
        <div className="grid grid-cols-2 gap-1.5" data-testid="meas-client-mode">
          <button onClick={() => setClientMode("new")} data-testid="meas-client-new" className={`py-2.5 text-[11px] font-bold uppercase tracking-widest border rounded-lg transition-colors duration-200 ${clientMode === "new" ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/55"}`}>New customer</button>
          <button onClick={() => setClientMode("existing")} data-testid="meas-client-existing" className={`py-2.5 text-[11px] font-bold uppercase tracking-widest border rounded-lg transition-colors duration-200 ${clientMode === "existing" ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/55"}`}>Find existing</button>
        </div>
        {clientMode === "existing" && (
        <select onChange={(e) => { applyPrefill(e.target.value); e.target.value = ""; }} data-testid="meas-prefill" defaultValue="" className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-4 text-base text-white focus:outline-none focus:border-primary appearance-none">
          <option value="" className="bg-[#252720]">Pick a customer or proposal…</option>
          {prefill.customers.length > 0 && (
            <optgroup label="Customers" className="bg-[#252720]">
              {prefill.customers.map((c) => <option key={c.id} value={`cust:${c.id}`} className="bg-[#252720]">{c.name}{c.address ? ` — ${c.address}` : ""}</option>)}
            </optgroup>
          )}
          {prefill.proposals.length > 0 && (
            <optgroup label="Proposals" className="bg-[#252720]">
              {prefill.proposals.map((p) => <option key={p.id} value={`prop:${p.id}`} className="bg-[#252720]">{p.title} — {p.client_name}</option>)}
            </optgroup>
          )}
        </select>
        )}
        <Field label="Project name"><input value={sheet.project_name} onChange={(e) => upd({ project_name: e.target.value })} placeholder="e.g. Colonial re-side — Stamford" data-testid="meas-project-name" className={inputCls} /></Field>
        <Field label="Customer"><input value={sheet.client_name} onChange={(e) => upd({ client_name: e.target.value })} placeholder="Customer name" data-testid="meas-client-name" className={inputCls} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone"><input value={sheet.client_phone} onChange={(e) => upd({ client_phone: e.target.value })} placeholder="(203) …" data-testid="meas-phone" className={inputCls} /></Field>
          <Field label="Email"><input value={sheet.client_email} onChange={(e) => upd({ client_email: e.target.value })} placeholder="email@…" data-testid="meas-email" className={inputCls} /></Field>
        </div>
        <Field label="Project address (job site)"><input value={sheet.address} onChange={(e) => upd({ address: e.target.value })} placeholder="Where the work happens" data-testid="meas-address" className={inputCls} /></Field>
        <Field label="Billing address (if different)"><input value={sheet.billing_address} onChange={(e) => upd({ billing_address: e.target.value })} placeholder="Where invoices go" data-testid="meas-billing" className={inputCls} /></Field>
        <Field label="Notes"><textarea value={sheet.notes} onChange={(e) => upd({ notes: e.target.value })} placeholder="Gate code, parking, access notes…" rows={2} data-testid="meas-notes" className={inputCls} /></Field>
      </section>

      {/* 2 — ITEMS & MEASUREMENTS (category -> item -> expression rows) */}
      <section className="space-y-4">
        <SectionTitle sub="Choose a category, then the library item — measurements go under it. Type the math straight in: 12 x 15 + 13 x 12, or (20 x 8) / 2 for a gable.">2 · ITEMS & MEASUREMENTS</SectionTitle>

        {sheetCats.map((cat) => {
          const catItems = sheet.items.filter((i) => i.category === cat);
          const closed = closedCats[cat];
          /* category footer: totals per unit family, waste once, subtotal */
          const units = {};
          let catSubtotal = 0;
          catItems.forEach((it) => {
            const c = calcs[it.id];
            if (!c) return;
            catSubtotal = round2(catSubtotal + c.line);
            if (c.family === "flat") return;
            const u = units[FAMILY_UNIT[c.family]] || (units[FAMILY_UNIT[c.family]] = { gross: 0, ded: 0, net: 0, final: 0 });
            u.gross = round2(u.gross + c.gross);
            u.ded = round2(u.ded + c.ded);
            u.net = round2(u.net + c.net);
            u.final = round2(u.final + c.withWaste);
          });
          return (
            <div key={cat} className="border border-white/10 rounded-xl bg-card overflow-hidden" data-testid={`meas-cat-${cat}`}>
              <button onClick={() => setClosedCats({ ...closedCats, [cat]: !closed })} data-testid={`meas-cat-toggle-${cat}`} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-white/5 transition-colors duration-200">
                <span className="font-display text-lg tracking-wide uppercase">{cat} <span className="text-white/35 text-xs font-sans normal-case tracking-normal">· {catItems.length} item{catItems.length === 1 ? "" : "s"}</span></span>
                <span className="flex items-center gap-2.5">
                  <span className="text-primary font-bold text-sm" data-testid={`meas-cat-subtotal-${cat}`}>{money(catSubtotal)}</span>
                  <ChevronDown size={17} className={`text-white/40 transition-transform duration-300 ${closed ? "" : "rotate-180"}`} aria-hidden="true" />
                </span>
              </button>
              {!closed && (
                <div className="px-3.5 pb-3.5 space-y-3">
                  {catItems.map((it) => {
                    const c = calcs[it.id];
                    const rowUnit = FAMILY_UNIT[c.family];
                    return (
                      <div key={it.id} className="border border-white/10 rounded-lg bg-background/60 p-3.5 space-y-2.5" data-testid={`meas-item-${it.id}`}>
                        {it.missing ? (
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm text-red-300">"{it.name}" was removed from the library — delete this row.</p>
                            <button onClick={() => upd({ items: sheet.items.filter((x) => x.id !== it.id) })} data-testid={`meas-item-del-${it.id}`} aria-label="Remove item" className="text-red-400 hover:text-red-300 p-1.5 shrink-0"><Trash2 size={16} /></button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-bold text-sm">{it.name}{it.subcategory ? ` — ${it.subcategory}` : ""}</p>
                                <p className="text-[11px] text-white/50 mt-0.5">{money(it.price)} / {it.unit}</p>
                              </div>
                              <button onClick={() => upd({ items: sheet.items.filter((x) => x.id !== it.id) })} data-testid={`meas-item-del-${it.id}`} aria-label="Remove item" className="text-red-400 hover:text-red-300 p-1.5 shrink-0"><Trash2 size={16} /></button>
                            </div>
                            {it.unit === "FLAT" ? (
                              <p className="text-xs text-white/50">Lump sum — no measurements needed. {money(it.price)}</p>
                            ) : (
                              <>
                                {(it.rows || []).map((r) => {
                                  const { value, error } = evalExpr(r.expression);
                                  return (
                                    <div key={r.id} className={`border rounded-lg p-2.5 space-y-1.5 ${error && error !== "empty" ? "border-red-400/50 bg-red-400/5" : "border-white/10 bg-white/[0.03]"}`} data-testid={`meas-row-${r.id}`}>
                                      <div className="flex items-center gap-2">
                                        <input value={r.label} onChange={(e) => updItem(it.id, { rows: it.rows.map((x) => (x.id === r.id ? { ...x, label: e.target.value } : x)) })} placeholder="Front Side, Gable…" data-testid={`meas-row-label-${r.id}`} className="flex-1 min-w-0 bg-transparent border-b border-white/10 focus:border-primary px-1 py-1.5 text-xs font-bold uppercase tracking-wider text-white/70 placeholder:text-white/30 focus:outline-none transition-colors" />
                                        <button onClick={() => updItem(it.id, { rows: it.rows.filter((x) => x.id !== r.id) })} data-testid={`meas-row-del-${r.id}`} aria-label="Delete measurement" className="text-red-400/70 hover:text-red-300 p-1 shrink-0"><Trash2 size={14} /></button>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <input value={r.expression} onChange={(e) => updItem(it.id, { rows: it.rows.map((x) => (x.id === r.id ? { ...x, expression: e.target.value } : x)) })} placeholder={c.family === "linear" ? "12 + 15 + 8" : c.family === "count" ? "4 + 2" : "12 x 15 + 13 x 12"} inputMode="text" data-testid={`meas-row-expr-${r.id}`} className="flex-1 min-w-0 bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-primary font-mono" />
                                        {error === "empty" ? null : error ? (
                                          <span className="shrink-0 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-300" data-testid={`meas-row-flag-${r.id}`}><AlertTriangle size={11} aria-hidden="true" /> incomplete</span>
                                        ) : (
                                          <span className="shrink-0 text-primary font-bold text-sm whitespace-nowrap" data-testid={`meas-row-value-${r.id}`}>= {qtyFmt(value)} {rowUnit}</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                <button onClick={() => updItem(it.id, { rows: [...(it.rows || []), { id: uid(), label: "", expression: "" }] })} data-testid={`meas-add-row-${it.id}`} className="w-full border border-dashed border-white/20 hover:border-primary transition-colors duration-300 py-2.5 text-[10px] font-bold uppercase tracking-widest text-white/60 hover:text-primary rounded-lg">
                                  + Add measurement
                                </button>
                                <p className="text-[11px] text-white/55 leading-relaxed border-t border-white/10 pt-2" data-testid={`meas-item-breakdown-${it.id}`}>
                                  {c.hasError && <span className="text-red-300 font-bold">Fix the flagged rows — they count as 0. </span>}
                                  Gross {qtyFmt(c.gross)} {rowUnit}
                                  {c.ded > 0 && <> − {qtyFmt(c.ded)} openings = <strong className="text-white">{qtyFmt(c.net)}</strong></>}
                                  {c.waste > 0 && <> + {c.waste}% waste = {qtyFmt(c.withWaste)}</>}
                                  {it.unit === "SQ" ? <> → <strong className="text-white">{qtyFmt(c.finalQty)} SQ</strong> (100 SQFT = 1 SQ)</> : <> → <strong className="text-white">{qtyFmt(c.finalQty)} {it.unit}</strong></>}
                                  {" "}× {money(it.price)} = <strong className="text-primary">{money(c.line)}</strong>
                                </p>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                  {/* category footer: totals per unit, waste once, subtotal */}
                  <div className="border-t border-white/10 pt-2.5 space-y-1.5" data-testid={`meas-cat-totals-${cat}`}>
                    {Object.entries(units).map(([u, t]) => (
                      <p key={u} className="text-[11px] text-white/55" data-testid={`meas-cat-units-${cat}-${u}`}>
                        <strong className="text-white/80">{u}</strong>: {qtyFmt(t.gross)} gross{t.ded > 0 ? ` − ${qtyFmt(t.ded)} = ${qtyFmt(t.net)} net` : ""}{num(sheet.category_waste[cat]) > 0 ? ` → ${qtyFmt(t.final)} with waste` : ""}
                      </p>
                    ))}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">Extra waste %</span>
                        <input type="number" inputMode="decimal" min="0" max="100" value={sheet.category_waste[cat] ?? ""} onChange={(e) => upd({ category_waste: { ...sheet.category_waste, [cat]: e.target.value } })} placeholder="0" data-testid={`meas-cat-waste-${cat}`} className={`${numInputCls} !w-20 !py-2 !text-sm`} />
                      </div>
                      <span className="text-xs text-white/60">Subtotal <strong className="text-primary">{money(catSubtotal)}</strong></span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!pickerOpen ? (
          <button onClick={() => setPickerOpen(true)} data-testid="meas-add-item" className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-4 transition-transform duration-300 hover:-translate-y-0.5">
            + Add item from library
          </button>
        ) : (
          <div className="border border-primary/40 bg-primary/5 rounded-xl p-4 space-y-3" data-testid="meas-picker">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-primary">
                {!pickCat ? "Pick a category" : !pickItem ? pickCat : pickItem.name}
              </p>
              <button onClick={() => { setPickerOpen(false); setPickCat(null); setPickItem(null); }} data-testid="meas-picker-close" className="text-white/50 hover:text-white text-xs font-bold uppercase tracking-widest">Close</button>
            </div>
            {!pickCat && (
              <div className="flex flex-wrap gap-1.5">
                {catOrder.map((c) => (
                  <button key={c} onClick={() => setPickCat(c)} data-testid={`meas-picker-cat-${c}`} className="px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider border border-white/15 text-white/70 hover:border-primary hover:text-primary transition-colors duration-200">{c}</button>
                ))}
              </div>
            )}
            {pickCat && !pickItem && (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                <button onClick={() => setPickCat(null)} className="text-[11px] font-bold uppercase tracking-widest text-white/50 hover:text-primary flex items-center gap-1"><ChevronDown size={12} className="rotate-90" /> Categories</button>
                {library.filter((l) => l.category === pickCat).map((l) => (
                  <button key={l.id} onClick={() => ((l.subcategories || []).length ? setPickItem(l) : addItem(l, null))} data-testid={`meas-picker-item-${l.id}`} className="w-full text-left border border-white/10 rounded-lg px-3 py-3 hover:border-primary/50 transition-colors duration-200">
                    <p className="font-bold text-sm">{l.name}</p>
                    <p className="text-[11px] text-white/40">{(l.subcategories || []).length ? `${l.subcategories.length} options — tap to choose` : `${money((l.labor_rate || 0) + (l.material_rate || 0) || l.default_rate || 0)} / ${l.unit}`}</p>
                  </button>
                ))}
                {library.filter((l) => l.category === pickCat).length === 0 && <p className="text-white/40 text-xs py-3">No items in this category yet — add them in General → Materials Library.</p>}
              </div>
            )}
            {pickItem && (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                <button onClick={() => setPickItem(null)} className="text-[11px] font-bold uppercase tracking-widest text-white/50 hover:text-primary flex items-center gap-1"><ChevronDown size={12} className="rotate-90" /> {pickCat}</button>
                {(pickItem.subcategories || []).map((o) => (
                  <button key={o.id} onClick={() => addItem(pickItem, o)} data-testid={`meas-picker-opt-${o.id}`} className="w-full text-left border border-white/10 rounded-lg px-3 py-3 hover:border-primary/50 transition-colors duration-200">
                    <p className="font-bold text-sm">{o.name}</p>
                    <p className="text-[11px] text-white/40">{money((o.labor_rate || 0) + (o.material_rate || 0))} / {pickItem.unit}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* 3 — DEDUCTIONS */}
      <section className="space-y-4">
        <SectionTitle sub="Windows, doors, openings. Enter one size + how many. Pick the category it applies to — and the items if not all of them. Counted once per item.">3 · AREA DEDUCTIONS</SectionTitle>
        {sheet.deductions.map((d) => {
          const direct = d.mode === "area";
          const catItems = sheet.items.filter((i) => i.category === d.category && familyOf(i.unit) === "area");
          return (
            <div key={d.id} className="border border-white/10 rounded-xl bg-card p-4 space-y-3" data-testid={`meas-ded-${d.id}`}>
              <div className="flex items-center gap-2">
                <input value={d.title} onChange={(e) => updDed(d.id, { title: e.target.value })} placeholder="Bedroom Window, Garage Door…" data-testid={`meas-ded-title-${d.id}`} className={inputCls} />
                <button onClick={() => upd({ deductions: sheet.deductions.filter((x) => x.id !== d.id) })} data-testid={`meas-ded-del-${d.id}`} aria-label="Delete deduction" className="text-red-400 hover:text-red-300 p-2 shrink-0"><Trash2 size={17} /></button>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => updDed(d.id, { mode: "wh" })} data-testid={`meas-ded-mode-${d.id}-wh`} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border ${!direct ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/50"}`}>Width × Height</button>
                <button onClick={() => updDed(d.id, { mode: "area" })} data-testid={`meas-ded-mode-${d.id}-area`} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border ${direct ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/50"}`}>Direct SQFT</button>
              </div>
              <div className="flex items-end gap-2">
                {!direct && (
                  <>
                    <Field label="Width (ft)"><input type="number" inputMode="decimal" min="0" value={d.width} onChange={(e) => updDed(d.id, { width: e.target.value })} data-testid={`meas-ded-width-${d.id}`} className={numInputCls} /></Field>
                    <Field label="Height (ft)"><input type="number" inputMode="decimal" min="0" value={d.height} onChange={(e) => updDed(d.id, { height: e.target.value })} data-testid={`meas-ded-height-${d.id}`} className={numInputCls} /></Field>
                  </>
                )}
                {direct && (
                  <Field label="Area of one opening (SQFT)"><input type="number" inputMode="decimal" min="0" value={d.area} onChange={(e) => updDed(d.id, { area: e.target.value })} data-testid={`meas-ded-area-${d.id}`} className={numInputCls} /></Field>
                )}
                <Field label="Qty"><input type="number" inputMode="decimal" min="0" value={d.quantity} onChange={(e) => updDed(d.id, { quantity: e.target.value })} data-testid={`meas-ded-qty-${d.id}`} className={numInputCls} /></Field>
                <span className="text-red-300 font-bold text-sm whitespace-nowrap pb-3" data-testid={`meas-ded-total-${d.id}`}>− {qtyFmt(dedTotal(d))} SQFT</span>
              </div>
              <div className="space-y-1.5">
                <Field label="Applies to category">
                  <select value={d.category || ""} onChange={(e) => updDed(d.id, { category: e.target.value, item_ids: [] })} data-testid={`meas-ded-category-${d.id}`} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-3 text-sm text-white focus:outline-none focus:border-primary appearance-none">
                    <option value="" className="bg-[#252720]">Choose category…</option>
                    {sheetCats.map((c) => <option key={c} value={c} className="bg-[#252720]">{c}</option>)}
                  </select>
                </Field>
                {d.category && catItems.length > 1 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-1.5">Items {(d.item_ids || []).length === 0 ? "(all selected — tap to narrow)" : ""}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {catItems.map((it) => {
                        const on = (d.item_ids || []).length === 0 || d.item_ids.includes(it.id);
                        return (
                          <button key={it.id} onClick={() => {
                            const cur = (d.item_ids || []).length === 0 ? catItems.map((x) => x.id) : d.item_ids;
                            const next = cur.includes(it.id) ? cur.filter((x) => x !== it.id) : [...cur, it.id];
                            updDed(d.id, { item_ids: next.length === catItems.length ? [] : next });
                          }} data-testid={`meas-ded-item-${d.id}-${it.id}`} className={`px-2.5 py-1.5 text-[10px] font-bold border rounded-lg transition-colors duration-200 ${on ? "bg-red-400/80 text-black border-red-400" : "border-white/15 text-white/55"}`}>
                            {it.name}{it.subcategory ? ` — ${it.subcategory}` : ""}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {d.category && catItems.length === 0 && (
                  <p className="text-[10px] text-amber-300/80">No area-priced items in this category — deductions only apply to SQFT/SQ items.</p>
                )}
              </div>
            </div>
          );
        })}
        <button onClick={() => upd({ deductions: [...sheet.deductions, { id: uid(), title: "", mode: "wh", width: "", height: "", area: "", quantity: 1, category: "", item_ids: [] }] })} data-testid="meas-add-deduction" className="w-full border-2 border-dashed border-white/20 hover:border-primary transition-colors duration-300 py-5 text-xs font-bold uppercase tracking-widest text-white/60 hover:text-primary">
          + Add deduction
        </button>
      </section>

      {/* 4 — ADJUSTMENTS */}
      <section className="space-y-4">
        <SectionTitle sub="Difficulty, risk, surprises. Folded into the item prices — the client never sees a separate line.">4 · PROJECT ADJUSTMENTS</SectionTitle>
        <div className="border border-white/10 rounded-xl bg-card p-4 grid grid-cols-2 gap-3">
          <Field label="Adjustment %"><input type="number" inputMode="decimal" min="0" max="100" value={sheet.adjustment_pct} onChange={(e) => upd({ adjustment_pct: e.target.value })} placeholder="0" data-testid="meas-adj-pct" className={numInputCls} /></Field>
          <Field label="Fixed amount $"><input type="number" inputMode="decimal" min="0" value={sheet.adjustment_fixed} onChange={(e) => upd({ adjustment_fixed: e.target.value })} placeholder="0" data-testid="meas-adj-fixed" className={numInputCls} /></Field>
        </div>
      </section>

      {/* TOTALS + ACTIONS */}
      <section className="border border-primary/40 bg-primary/5 rounded-xl p-5 space-y-2" data-testid="meas-totals">
        <div className="flex justify-between text-sm text-white/70"><span>Items subtotal</span><span data-testid="meas-subtotal">{money(totals.subtotal)}</span></div>
        {num(sheet.adjustment_pct) > 0 && <div className="flex justify-between text-sm text-white/70"><span>Project adjustment ({num(sheet.adjustment_pct)}%)</span><span data-testid="meas-adj-amount">{money(totals.adjAmt)}</span></div>}
        {num(sheet.adjustment_fixed) > 0 && <div className="flex justify-between text-sm text-white/70"><span>Fixed amount</span><span data-testid="meas-fixed-amount">{money(totals.fixed)}</span></div>}
        <div className="flex justify-between items-center border-t border-primary/30 pt-2 mt-1">
          <span className="font-display text-xl tracking-wide">ESTIMATE TOTAL</span>
          <span className="font-display text-3xl text-primary" data-testid="meas-total">{money(totals.total)}</span>
        </div>
        <button onClick={createEstimate} disabled={estimating || saving} data-testid="meas-create-estimate" className="w-full mt-3 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm py-4 flex items-center justify-center gap-2 transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50">
          <FileCheck size={16} aria-hidden="true" />
          {estimating ? "Working..." : sheet.linked_proposal_id ? "Update Linked Estimate" : "Create Estimate"}
        </button>
        <p className="text-[11px] text-white/40 text-center">Creates a hidden draft in Proposals — review, edit and send it there. Saving again updates the same estimate.</p>
        {sheet.id && delPin === null && (
          <button onClick={() => setDelPin("")} data-testid="meas-delete" className="w-full text-red-400/70 hover:text-red-300 text-[11px] font-bold uppercase tracking-widest pt-1">Delete this sheet</button>
        )}
        {sheet.id && delPin !== null && (
          <div className="border border-red-400/40 bg-red-400/5 rounded-lg p-3 mt-2 space-y-2" data-testid="meas-delete-confirm">
            <p className="text-[11px] text-red-200/80 leading-relaxed">Delete this measurement sheet? A linked estimate is kept. Enter the manager PIN to confirm.</p>
            <div className="flex gap-2">
              <input value={delPin} onChange={(e) => setDelPin(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="Manager PIN" inputMode="numeric" type="password" autoFocus data-testid="meas-delete-pin" className={inputCls} />
              <button onClick={removeSheet} data-testid="meas-delete-go" className="px-4 bg-red-500/90 hover:bg-red-500 text-white text-[11px] font-bold uppercase tracking-widest rounded-lg transition-colors duration-200">Delete</button>
              <button onClick={() => setDelPin(null)} data-testid="meas-delete-cancel" className="px-3 text-[11px] font-bold uppercase tracking-widest text-white/50 hover:text-white">Cancel</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
